import { add, format, getMonth, isValid, parse, startOfDay, startOfWeek } from "date-fns";
import { dateFnsLocaleFromLang, localeFromLang, uppercaseForLang, type Language } from "../../../src/api/langConfig";

export const DATE_FORMAT = 'yyyy_MM_dd'
export const DATE_TIME_FORMAT = 'yyyy_MM_dd hh:mm'

/** Canonical RTDB order day keys (`yyyy_MM_dd`). */
export const ORDER_DAY_KEY_REGEX = /^\d{4}_\d{2}_\d{2}$/;

/** Legacy order day keys (`yyyy-MM-dd`) — same shape as {@link ORDER_DAY_KEY_REGEX}, hyphens instead of underscores. */
export const ORDER_DAY_KEY_LEGACY_REGEX = new RegExp(ORDER_DAY_KEY_REGEX.source.replace(/_/g, '-'));

/** Server order day keys use underscores; legacy fund-activity backfill used `yyyy-MM-dd`. */
export function normalizeOrderDayKey(day: string): string {
    if (ORDER_DAY_KEY_LEGACY_REGEX.test(day)) {
        return day.replace(/-/g, "_");
    }
    return day;
}

/** Europe/Athens calendar day start for admin checkout-incident backfill (`yyyy-MM-dd` or `yyyy_MM_dd`). */
export function backfillRangeStartMsFromDayKey(day: string): number | null {
    const normalized = normalizeOrderDayKey(day.trim());
    const m = ORDER_DAY_KEY_REGEX.exec(normalized);
    if (!m) {
        return null;
    }
    const [, y, mo, d] = m;
    return Date.UTC(Number(y), Number(mo) - 1, Number(d), -3, 0, 0, 0);
}

/** Europe/Athens end of calendar day for admin checkout-incident backfill (`yyyy-MM-dd` or `yyyy_MM_dd`). */
export function backfillRangeEndMsFromDayKey(day: string): number | null {
    const normalized = normalizeOrderDayKey(day.trim());
    const m = ORDER_DAY_KEY_REGEX.exec(normalized);
    if (!m) {
        return null;
    }
    const [, y, mo, d] = m;
    return Date.UTC(Number(y), Number(mo) - 1, Number(d), 20, 59, 59, 999);
}

/** Admin grids / exports: date + time; locales match {@link formatAdminListDate} (el-GR / en-US). */
export function formatAdminListDateTime(timestamp: number, lang: string): string {
    return new Date(Number(timestamp)).toLocaleString(localeFromLang(lang as Language), {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });
}

/** User-facing label for an order day key ({@link DATE_FORMAT} or legacy `yyyy-MM-dd`). */
export function formatOrderDayLabel(orderDate: string, lang: string): string {
    try {
        const date = toDateFromServer(orderDate);
        if (Number.isNaN(date.getTime())) return orderDate;
        return date.toLocaleDateString(localeFromLang(lang as Language), {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    } catch {
        return orderDate;
    }
}

/** User-facing date for when an order was placed (checkout timestamp). */
export function formatOrderPlacedDateLabel(timestamp: number, lang: string): string {
    return new Date(Number(timestamp)).toLocaleDateString(localeFromLang(lang as Language), {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
}

/** User-facing history rows (order app wallet activity, etc.): short month + time. */
export function formatUserActivityDateTime(timestamp: number, lang: string): string {
    return new Date(Number(timestamp)).toLocaleString(localeFromLang(lang as Language), {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

/** Admin exports: localized date-only string (`dd/mm/yyyy` el, `m/d/yyyy` en-US). */
export function formatAdminListDate(date: Date, lang: string): string {
    return date.toLocaleDateString(localeFromLang(lang as Language), { day: "2-digit", month: "2-digit", year: "numeric" });
}

/** User-facing short date (order app news): `dd/mm/yy` el-GR / en-US. */
export function formatNewsDate(timestamp: number, lang: string): string {
    return new Date(Number(timestamp)).toLocaleDateString(localeFromLang(lang as Language), {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
    });
}

export function getWeekDays(date: Date | string) {
    if (!date) {
        throw new Error(`Invalid date: ${date}`);
    }

    if (typeof date === 'string') {
        date = toDateFromServer(date);
    }
    if (date.getDay() === 0)
        date = add(date, { days: -1 });

    const first = startOfWeek(date);
    const dates = [1, 2, 3, 4, 5]
        .map(inc => add(first, { days: inc }))
        .map(toDayString)

    return dates;
}

/**
 * Mon–Fri strips from `getWeekDays` that **include at least one weekday** falling inside
 * the calendar month `{ year, monthIndex0 }`. (Weekends in the month do not pull in a
 * strip whose Mon–Fri are all in the previous/next month.)
 */
export function getWeekArraysOverlappingCalendarMonth(year: number, monthIndex0: number): string[][] {
    const monthStart = new Date(year, monthIndex0, 1);
    const monthEnd = new Date(year, monthIndex0 + 1, 0);
    const weekByMonday = new Map<string, string[]>();
    for (let d = new Date(monthStart); d <= monthEnd; d = add(d, { days: 1 })) {
        const week = getWeekDays(d);
        weekByMonday.set(week[0], week);
    }
    const weekdayInSelectedMonth = (dayStr: string) => {
        const t = toDateFromServer(dayStr);
        return t.getFullYear() === year && t.getMonth() === monthIndex0;
    };
    return Array.from(weekByMonday.values())
        .filter((week) => week.some((dayStr) => weekdayInSelectedMonth(dayStr)))
        .sort((a, b) => a[0].localeCompare(b[0]));
}

/** Flattened Mon–Fri day strings for admin menu / print for one calendar month. */
export function getAllMenuWeekDaysOverlappingCalendarMonth(year: number, monthIndex0: number): string[] {
    return getWeekArraysOverlappingCalendarMonth(year, monthIndex0).flat();
}

/**
 * First Monday on or after the 1st of the calendar month containing `monthAnchor`.
 * If that Monday falls in the previous month (e.g. month starts on Tue), use the next Monday.
 */
export function getFirstMondayOfCalendarMonth(monthAnchor: Date): Date {
    const firstDay = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth(), 1);
    const firstMonday = startOfWeek(firstDay, { weekStartsOn: 1 });
    if (firstMonday.getMonth() !== monthAnchor.getMonth()) {
        return add(firstMonday, { days: 7 });
    }
    return firstMonday;
}

/** Mon–Fri day keys from the month's first Monday through the last calendar day of that month. */
export function getMenuWeekdaysFirstMondayThroughMonthEnd(year: number, monthIndex0: number): string[] {
    const monthAnchor = new Date(year, monthIndex0, 1);
    const monthEnd = new Date(year, monthIndex0 + 1, 0);
    const days: string[] = [];
    for (let d = getFirstMondayOfCalendarMonth(monthAnchor); d <= monthEnd; d = add(d, { days: 1 })) {
        const dow = d.getDay();
        if (dow >= 1 && dow <= 5) {
            days.push(toDayString(d));
        }
    }
    return days;
}

/** `yyyy_MM_dd` for the first day of the calendar month containing `asOf` (draft prune keep-from). */
export function getCurrentCalendarMonthStartDayString(asOf: Date = new Date()): string {
    return toDayString(new Date(asOf.getFullYear(), asOf.getMonth(), 1));
}

/** `yyyy_MM_dd` for the first day of the calendar month before the month containing `asOf`. */
export function getPreviousCalendarMonthStartDayString(asOf: Date = new Date()): string {
    return toDayString(new Date(asOf.getFullYear(), asOf.getMonth() - 1, 1));
}

/** True when a server day key (`yyyy_MM_dd`) is strictly before `asOf` at local midnight. */
export function isPastServerDay(day: string, asOf: Date = new Date()): boolean {
    return startOfDay(toDateFromServer(day)) < startOfDay(asOf);
}

export function getAllWeekDays(date: Date | string) {
    if (!date) {
        throw new Error(`Invalid date: ${date}`);
    }

    if (typeof date === 'string') {
        date = toDateFromServer(date);
    }
    if (date.getDay() === 0)
        date = add(date, { days: -1 });

    const first = startOfWeek(date);
    const dates = [1, 2, 3, 4, 5, 6, 7]
        .map(inc => add(first, { days: inc }))
        .map(toDayString)

    return dates;
}

export function toDateFromServer(day: string): Date {
    if (!day || typeof day !== 'string') {
        throw new Error(`Invalid date string: ${day}`);
    }
    return parse(normalizeOrderDayKey(day), DATE_FORMAT, Date.now());
}

/** False while MUI DatePicker field sections are mid-edit (Invalid Date). */
export function isValidJsDate(date: Date | null | undefined): date is Date {
    return date instanceof Date && isValid(date);
}

export function toServerFromDate(date: Date): string {
    return format(date, DATE_FORMAT);
}


export function toDateTimeFromServer(day: string): Date {
    if (!day || typeof day !== 'string') {
        throw new Error(`Invalid date time string: ${day}`);
    }
    return parse(day, DATE_TIME_FORMAT, Date.now());
}

export function toServerFromDateTime(date: Date): string {
    return format(date, DATE_TIME_FORMAT);
}

export function todayToString(): string {

    return toDayString(new Date());
}

export function tomorrowToDate(): Date {
    var tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
}
export function tomorrowToString(): string {
    return toDayString(tomorrowToDate());
}

export function getNextMonth(): number {
    const now = new Date();
    return getMonth(new Date(now.getFullYear(), now.getMonth() + 1, 1));
}

export function addDays(date: Date, numOfDays: number): string {
    return toDayString(add(date, { days: numOfDays }));
}

export function getStartOfWeekMonday(date: Date): Date {
    return startOfWeek(date, { weekStartsOn: 1 });
}

export function toDayString(date: Date) {
    return format(date, DATE_FORMAT);
}

/** Bounds for `orderByKey().startAfter` / `endBefore` when loading menu day nodes (`yyyy_MM_dd`). */
export function getMenuDayKeysRangeBounds(days: string[]): { startAfterKey: string; endBeforeKey: string } | null {
    if (days.length === 0) return null;
    const firstDay = days[0];
    const lastDay = days[days.length - 1];
    return {
        startAfterKey: toDayString(add(toDateFromServer(firstDay), { days: -1 })),
        endBeforeKey: toDayString(add(toDateFromServer(lastDay), { days: 1 })),
    };
}

/** School year runs September (month 8) to July (month 6 of next calendar year). Returns the calendar year when the current school year started. */
export function getCurrentSchoolYearStartYear(asOf: Date = new Date()): number {
    const year = asOf.getFullYear();
    const month = asOf.getMonth(); // 0-11
    return month >= 8 ? year : year - 1;
}

/** First day of the current school year (September 1). */
export function getCurrentSchoolYearStartDate(asOf: Date = new Date()): Date {
    const startYear = getCurrentSchoolYearStartYear(asOf);
    return new Date(startYear, 8, 1); // month 8 = September
}

/**
 * Minimum `start` string for Firebase `orderByChild('start').startAt(...)` on meal package catalog.
 * Uses the current school year (Sept → following summer); includes packages that span two calendar years.
 */
export function getSchoolYearPackageCatalogStartAt(asOf: Date = new Date()): string {
    return toDayString(getCurrentSchoolYearStartDate(asOf));
}

/** Whether to show next school year in selectors (from August, month 7, of current school year's end year). */
export function getShouldShowNextSchoolYear(asOf: Date = new Date()): boolean {
    const year = asOf.getFullYear();
    const month = asOf.getMonth();
    const currentSchoolYearEndYear = getCurrentSchoolYearStartYear(asOf) + 1;
    return month >= 7 && year >= currentSchoolYearEndYear;
}

/** School year start year for admin monthly menu month picker (Sep–Jul). From August of the end calendar year, use the upcoming school year. */
export function getAdminMenuSelectorSchoolYearStart(asOf: Date = new Date()): number {
    const start = getCurrentSchoolYearStartYear(asOf);
    return getShouldShowNextSchoolYear(asOf) ? start + 1 : start;
}

/** Month keys for one school year in admin pickers (Aug–Jul; same months as wallet school year). */
export function getSchoolYearMonthKeysForSelector(schoolYearStartYear: number, _asOf: Date = new Date()): string[] {
    return getSchoolYearMonthKeys(schoolYearStartYear);
}

/** `yyyy-MM` month keys for one wallet-aligned school year (August through July). `schoolYearStartYear` = August calendar year (e.g. 2025 → 2025-08 … 2026-07). */
export function getSchoolYearMonthKeys(schoolYearStartYear: number): string[] {
    const keys: string[] = [];
    for (let month = 7; month <= 11; month++) {
        keys.push(`${schoolYearStartYear}-${String(month + 1).padStart(2, '0')}`);
    }
    for (let month = 0; month <= 6; month++) {
        keys.push(`${schoolYearStartYear + 1}-${String(month + 1).padStart(2, '0')}`);
    }
    return keys;
}

/** Month keys for admin monthly menu picker. In August, next school year (Sep–Jul) plus August for menu entry. */
export function getAdminMenuMonthKeys(asOf: Date = new Date()): string[] {
    return getSchoolYearMonthKeysForSelector(getAdminMenuSelectorSchoolYearStart(asOf), asOf);
}

/** Default `yyyy-MM` for admin monthly menu month picker (next calendar month). */
export function getDefaultAdminMenuSelectedMonth(asOf: Date = new Date()): string {
    const next = new Date(asOf.getFullYear(), asOf.getMonth() + 1, 1);
    return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`;
}

/** Pick default month when `preferred` may not be in `availableKeys` (next month, then next listed, then last listed). */
export function resolveAdminMenuSelectedMonth(asOf: Date, availableKeys: string[]): string {
    const preferred = getDefaultAdminMenuSelectedMonth(asOf);
    if (availableKeys.includes(preferred)) return preferred;
    const nextOrLater = availableKeys.find((key) => key >= preferred);
    return nextOrLater ?? availableKeys[availableKeys.length - 1] ?? preferred;
}

/** Wallet / transaction history school year: 1 Aug – 31 Jul. Returns the August calendar year when the year started. */
export function getWalletHistorySchoolYearStartYear(asOf: Date = new Date()): number {
    const year = asOf.getFullYear();
    const month = asOf.getMonth(); // 0-11; August = 7
    return month >= 7 ? year : year - 1;
}

/** Inclusive millisecond bounds for wallet history queries (`orderByChild('timestamp')`). */
export function getWalletHistorySchoolYearBounds(startYear: number): { startMs: number; endMs: number } {
    const startMs = new Date(startYear, 7, 1, 0, 0, 0, 0).getTime();
    const endMs = new Date(startYear + 1, 6, 31, 23, 59, 59, 999).getTime();
    return { startMs, endMs };
}

/** Inclusive Firebase day strings for a calendar year (1 Jan – 31 Dec). */
export function getCalendarYearServerDayBounds(calendarYear: number): { firstDay: string; lastDay: string } {
    return {
        firstDay: `${calendarYear}_01_01`,
        lastDay: `${calendarYear}_12_31`,
    };
}

/** `yyyyMM` month ids for January through December of a calendar year. */
export function getCalendarYearMonthIds(calendarYear: number): string[] {
    return Array.from({ length: 12 }, (_, index) => {
        const month = index + 1;
        return `${calendarYear}${String(month).padStart(2, "0")}`;
    });
}

/** Inclusive Firebase day strings for school-year order stats (1 Aug – 31 Jul). */
export function getSchoolYearOrderStatsServerDayBounds(schoolYearStartYear: number): { firstDay: string; lastDay: string } {
    return {
        firstDay: `${schoolYearStartYear}_08_01`,
        lastDay: `${schoolYearStartYear + 1}_07_31`,
    };
}

/** `yyyyMM` ids for saved monthly order stats covering one school year (August–July). */
export function getSchoolYearMonthlyOrderStatsMonthIds(schoolYearStartYear: number): string[] {
    return getSchoolYearMonthKeys(schoolYearStartYear).map((monthKey) => monthKey.replace(/-/g, ""));
}

/** True after 31 July of the school year's end calendar year. */
export function isSchoolYearCompleteForOrderStats(schoolYearStartYear: number, asOf: Date = new Date()): boolean {
    const endMs = new Date(schoolYearStartYear + 1, 6, 31, 23, 59, 59, 999).getTime();
    return asOf.getTime() > endMs;
}

/** One calendar month within a wallet school year (Aug–Jul), for per-month exports. */
export type WalletHistorySchoolYearMonth = {
    /** 0 = August … 11 = July */
    monthIndex: number;
    startMs: number;
    endMs: number;
    /** Excel-safe tab name, e.g. `2025-08` */
    sheetName: string;
};

/** Twelve inclusive month windows covering the school year (August through July). */
export function getWalletHistorySchoolYearMonths(startYear: number): WalletHistorySchoolYearMonth[] {
    return Array.from({ length: 12 }, (_, monthIndex) => {
        const calendarMonth = (7 + monthIndex) % 12;
        const calendarYear = calendarMonth >= 7 ? startYear : startYear + 1;
        const startMs = new Date(calendarYear, calendarMonth, 1, 0, 0, 0, 0).getTime();
        const endMs = new Date(calendarYear, calendarMonth + 1, 0, 23, 59, 59, 999).getTime();
        const sheetName = `${calendarYear}-${String(calendarMonth + 1).padStart(2, "0")}`;
        return { monthIndex, startMs, endMs, sheetName };
    });
}

/** Inclusive order-day key bounds for order history queries (`orderByKey()` on `yyyy_MM_dd`). */
export function getWalletHistorySchoolYearOrderDateBounds(startYear: number): { startKey: string; endKey: string } {
    return {
        startKey: toServerFromDate(new Date(startYear, 7, 1)),
        endKey: toServerFromDate(new Date(startYear + 1, 6, 31)),
    };
}

/** Calendar month bounds for wallet meal stats (`yyyy_MM_dd`, inclusive). */
export function getCalendarMonthOrderDateBounds(calendarYear: number, calendarMonth: number): { startKey: string; endKey: string } {
    return {
        startKey: toServerFromDate(new Date(calendarYear, calendarMonth - 1, 1)),
        endKey: toServerFromDate(new Date(calendarYear, calendarMonth, 0)),
    };
}

/** Calendar month bounds for wallet ledger timestamps (local month, inclusive). */
export function getCalendarMonthTimestampBoundsMs(calendarYear: number, calendarMonth: number): { startMs: number; endMs: number } {
    const startMs = new Date(calendarYear, calendarMonth - 1, 1, 0, 0, 0, 0).getTime();
    const endMs = new Date(calendarYear, calendarMonth, 0, 23, 59, 59, 999).getTime();
    return { startMs, endMs };
}

/** Display label e.g. `2024–25`. */
export function formatWalletHistorySchoolYearLabel(startYear: number): string {
    return `${startYear}–${String(startYear + 1).slice(-2)}`;
}

/** School year start (Aug year) for a wallet ledger timestamp. */
export function getWalletHistorySchoolYearStartYearFromTimestamp(timestamp: number): number {
    return getWalletHistorySchoolYearStartYear(new Date(timestamp));
}

/** School years from registration through current (current first). */
export function getWalletHistorySchoolYearRange(earliestStartYear: number, latestStartYear: number): number[] {
    if (earliestStartYear > latestStartYear) return [latestStartYear];
    const count = latestStartYear - earliestStartYear + 1;
    return Array.from({ length: count }, (_, i) => latestStartYear - i);
}

/** School year start year (Aug–Jul) for a server day key (`yyyy_MM_dd`). */
export function getSchoolYearStartYearForServerDay(day: string): number {
    return getWalletHistorySchoolYearStartYear(toDateFromServer(day));
}

/** School year for a school-closed range from its start day; incomplete ranges return null. */
export function getSchoolClosedRangeSchoolYearStartYear(rangeStartDay: string | null | undefined): number | null {
    if (!rangeStartDay || typeof rangeStartDay !== 'string') return null;
    return getSchoolYearStartYearForServerDay(rangeStartDay);
}

/** Last calendar day of the month after the given date (e.g. for range limits). */
export function getLastDayOfNextMonth(asOf: Date = new Date()): Date {
    return new Date(asOf.getFullYear(), asOf.getMonth() + 2, 0);
}

/** True when `yyyy-MM` month key is strictly before the current month (or `asOf` month). */
export function isPastMonth(monthKey: string, asOf: Date = new Date()): boolean {
    const selectedMonthStart = toDateFromServer(`${monthKey.replace("-", "_")}_01`);
    const currentMonthStart = toDateFromServer(`${toDayString(asOf).slice(0, 7)}_01`);
    return selectedMonthStart < currentMonthStart;
}

/** School order deadlines use Greece civil time — not the device or Cloud Functions host OS timezone. */
export const ORDER_DEADLINE_TIMEZONE = 'Europe/Athens';

export type OrderDeadlineClock = {
    /** Civil "today" in {@link ORDER_DEADLINE_TIMEZONE}, at local midnight (for date-fns day math). */
    calendarDate: Date;
    /** Hour 0–23 in {@link ORDER_DEADLINE_TIMEZONE}. */
    hour: number;
};

/** Current calendar date + hour used for order deadline rules (`orderTime` / `orderMenuTime` cutoffs). */
export function getOrderDeadlineClock(at: Date = new Date()): OrderDeadlineClock {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: ORDER_DEADLINE_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        hour12: false,
    }).formatToParts(at);
    const pick = (type: Intl.DateTimeFormatPartTypes): number => {
        const raw = parts.find(p => p.type === type)?.value ?? '0';
        const n = parseInt(raw, 10);
        return Number.isFinite(n) ? n : 0;
    };
    const year = pick('year');
    const month = pick('month');
    const day = pick('day');
    const hour = pick('hour') % 24;
    return {
        calendarDate: startOfDay(new Date(year, month - 1, day)),
        hour,
    };
}

export function dayNum(day: string): number {
    if (!day || typeof day !== 'string') {
        throw new Error(`Invalid day string: ${day}`);
    }
    return +format(toDateFromServer(day), 'i');
}

export function orderMenuDate(orderTime: number, orderDays: number, orderSpecificDay: number, at: Date = new Date()) {

    if (typeof orderSpecificDay === 'number' && orderSpecificDay >= 0)
        return orderDateWeekBefore(orderTime, orderSpecificDay, at);
    return orderDateDays(orderTime, orderDays, at);
}

// Orders for next week close at a specific Day and time, e.x. Thurdsay 22:00
function orderDateWeekBefore(orderTime: number, orderSpecificDay: number, at: Date = new Date()) {

    const { calendarDate: now, hour } = getOrderDeadlineClock(at);
    const day = hour < orderTime ? now : add(now, { days: 1 });
    let weekdayNow = day.getDay();

    let daysToAdd: number;
    if (orderSpecificDay === 0)
        daysToAdd = 7 - weekdayNow;
    else
        daysToAdd = weekdayNow <= orderSpecificDay ? 7 - weekdayNow : weekdayNow - orderSpecificDay + 7;
    return startOfDay(add(now, { days: hour < orderTime ? daysToAdd : daysToAdd + 1 }));
}
// order time: 0-23. Orders after this time (i.e. 11 for 11:00, 22 for 22:00) are for the next day
// orderDays: Number of days before the order date (0 for same day, 1 for day before, etc.)
export function orderDateDays(orderTime: number, orderDays: number, at: Date = new Date()) {
    const { calendarDate: now, hour } = getOrderDeadlineClock(at);

    // For same-day ordering (orderDays = 0), orders close at the specified time on the same day
    if (orderDays === 0) {
        let day = now;
        // If current hour is past order time, orders are closed for today
        if (hour >= orderTime) {
            day = add(now, { days: 1 });
        }
        return startOfDay(day);
    }

    // For advance ordering (orderDays > 0), orders close orderDays before the target date
    let day = add(now, { days: orderDays });
    day = hour < orderTime ? day : add(day, { days: 1 });
    // Orders from Thursday after 22:00 till Sunday before 22:00 are for Monday
    if (day.getDay() == 6)
        day = add(day, { days: 2 });
    else if (day.getDay() == 0)
        day = add(day, { days: 1 });
    return startOfDay(day);
}

/** Coerce admin `orderFutureWeeks` (0 = current week only, 1 = +one future week, …). */
export function normalizeOrderFutureWeeks(orderFutureWeeks: unknown): number {
    if (typeof orderFutureWeeks === 'number' && Number.isFinite(orderFutureWeeks) && orderFutureWeeks >= 0) {
        return Math.floor(orderFutureWeeks);
    }
    if (typeof orderFutureWeeks === 'string' && orderFutureWeeks.trim() !== '') {
        const n = Number(orderFutureWeeks);
        if (Number.isFinite(n) && n >= 0) {
            return Math.floor(n);
        }
    }
    return 0;
}

/** Calendar days ahead parents can order — `(orderFutureWeeks + 1) × 7` (basket day-count / scan horizon). */
export function getOrderFutureDays(orderFutureWeeks: number | null | undefined): number {
    const weeks = normalizeOrderFutureWeeks(orderFutureWeeks);
    return (weeks + 1) * 7;
}

/** Last calendar day (`DATE_FORMAT`) for basket horizon checks (not per-day order deadlines — see `orderDeadlines.ts`). */
export function getMaxOrderableDay(orderFutureWeeks: number | null | undefined, at: Date = new Date()): string {
    const { calendarDate } = getOrderDeadlineClock(at);
    return toDayString(add(calendarDate, { days: getOrderFutureDays(orderFutureWeeks) }));
}

/** Whether the order app date bar may show the Mon–Fri strip one week after `displayedWeekMondayDay`. */
export function canNavigateToNextOrderWeek(
    displayedWeekMondayDay: string,
    orderFutureWeeks: unknown,
    at: Date = new Date(),
): boolean {
    const futureWeeks = normalizeOrderFutureWeeks(orderFutureWeeks);
    const { calendarDate } = getOrderDeadlineClock(at);
    const currentWeekMonday = toDateFromServer(getWeekDays(calendarDate)[0]);
    const displayedMonday = toDateFromServer(displayedWeekMondayDay);
    const nextWeekMonday = add(displayedMonday, { days: 7 });
    const lastAllowedMonday = add(currentWeekMonday, { days: futureWeeks * 7 });
    return nextWeekMonday.getTime() <= lastAllowedMonday.getTime();
}

// Orders at least 2 days before
// Currently not used, as we offer everything from the day before
// If we want to add again 2 days gap, call this function instead of orderDate in MealsPopup.tsx and Order.tsx
export function orderMealDate(orderTime: number) {
    const now = Date.now();
    const hour = +format(now, 'H');
    let day = hour < orderTime ? new Date() : add(now, { days: 1 });

    // Thursday to Sunday can order menu meals for Monday

    // Thursday orders for Monday
    if (day.getDay() === 4)
        day = add(day, { days: 4 });
    // Friday orders for Monday
    else if (day.getDay() === 5)
        day = add(day, { days: 3 });
    // Saturday orders for Monday
    else if (day.getDay() === 6)
        day = add(day, { days: 2 });
    // Sunday orders for Monday
    else if (day.getDay() === 0)
        day = add(day, { days: 1 });
    // Monday-Wednesday orders for 2 days later
    else
        day = add(day, { days: 2 });
    return startOfDay(day);
}

export function adminOrderDate(date: Date, orderTime: number) {

    const { calendarDate, hour } = getOrderDeadlineClock(date);
    let day = hour < orderTime ? calendarDate : add(calendarDate, { days: 1 });

    if (day.getDay() == 6)
        day = add(day, { days: 2 });
    else if (day.getDay() == 0)
        day = add(day, { days: 1 });

    return startOfDay(day);
}
export function adminOrderDateNow(orderTime: number, at: Date = new Date()) {
    return adminOrderDate(at, orderTime);
}

export function formatOrderDateHeader(loc: string, dateOrder: Date) {

    let orderDateStr: string;
    orderDateStr = format(dateOrder, "PPPP", { locale: dateFnsLocaleFromLang(loc as Language) }) ?? '...';
    orderDateStr = orderDateStr.substring(0, orderDateStr.length - 5) ?? '...';
    orderDateStr = orderDateStr.replace(/,([^,]*)$/, '' + '$1');
    return orderDateStr;
}

export function formatOrderDateHeaderYear(loc: string, dateOrder: Date) {

    let orderDateStr: string;
    orderDateStr = format(dateOrder, "PPPP", { locale: dateFnsLocaleFromLang(loc as Language) }) ?? '...';
    orderDateStr = orderDateStr.replace(/,([^,]*)$/, '' + '$1');
    return orderDateStr;
}

export function formatOrderWeekHeaderYear(loc: string, dateOrder: Date) {

    let orderDateStr: string;
    orderDateStr = format(dateOrder, "PPPP", { locale: dateFnsLocaleFromLang(loc as Language) }) ?? '...';
    orderDateStr = orderDateStr.replace(/,([^,]*)$/, '' + '$1');
    return orderDateStr;
}

/** Stand-alone month title (date-fns `LLLL`); Greek nominative (Ιούνιος), not genitive (Ιουνίου). */
export function getMonthStandaloneName(lang: string, date: Date) {
    return format(date, "LLLL", { locale: dateFnsLocaleFromLang(lang as Language) });
}

/** Stand-alone month + year (e.g. Ιούνιος 2025, September 2025). */
export function getMonthStandaloneYearLabel(lang: string, date: Date): string {
    return `${getMonthStandaloneName(lang, date)} ${date.getFullYear()}`;
}

export function getMonthFullName(lang: string, date: Date) {
    return date.toLocaleString(localeFromLang(lang as Language), { month: 'long' });
}
export function getCurrentMonthFullName(lang: string) {
    return getMonthFullName(lang, new Date());
}
export function getMonthsList(lang: string) {
    const monthArray = [];
    for (let i = 0; i < 12; i++)
        monthArray.push(new Date(2025, i).toLocaleString(localeFromLang(lang as Language), { month: 'long' }));
    return monthArray;
}

/**
 * JS `Date.getDay()` weekday name (0 = Sun … 6 = Sat) via `Intl`.
 * Uses a fixed reference week so labels do not depend on “today”.
 */
export function getWeekdayName(dayNum: number, lang: string, style: 'long' | 'short' = 'long'): string {
    if (dayNum < 0 || dayNum > 6) {
        return '';
    }
    const monday = new Date(2025, 0, 6);
    const daysFromMonday = dayNum === 0 ? 6 : dayNum - 1;
    return add(monday, { days: daysFromMonday }).toLocaleDateString(localeFromLang(lang as Language), { weekday: style });
}

/** Mon–Fri short weekday labels for compact UI (aligned with `dayNum` / JS weekday order). */
export function getWeekdayAbbreviationsMonFri(lang: string): string[] {
    return [1, 2, 3, 4, 5].map((d) => uppercaseForLang(getWeekdayName(d, lang, 'short'), lang));
}

/** Twelve short month labels (calendar order), for compact UI. */
export function getMonthAbbreviations(lang: string): string[] {
    const out: string[] = [];
    for (let i = 0; i < 12; i++) {
        out.push(uppercaseForLang(new Date(2025, i, 1).toLocaleString(localeFromLang(lang as Language), { month: 'short' }), lang));
    }
    return out;
}

export function getDayAndMonth(date: Date) {
    return date.getDate() + "/" + (date.getMonth() + 1);
}

export function getCurrentYear() {
    return new Date().getFullYear();
}

export function toYearMonthString(date?: Date): string {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}${month}`;
}

export function getLastWeekday() {
    const dateNow = new Date();
    // Month is 0-based (January = 0, December = 11)
    let date = new Date(dateNow.getFullYear(), dateNow.getMonth() + 1, 0); // Last day of the month

    // If it's Saturday (6) or Sunday (0), go back to Friday
    while (date.getDay() === 0 || date.getDay() === 6)
        date.setDate(date.getDate() - 1);


    return date;
}

export function getLastWeekdayDate(dateNow: Date) {

    // Month is 0-based (January = 0, December = 11)
    let date = new Date(dateNow.getFullYear(), dateNow.getMonth() + 1, 0); // Last day of the month

    // If it's Saturday (6) or Sunday (0), go back to Friday
    while (date.getDay() === 0 || date.getDay() === 6)
        date.setDate(date.getDate() - 1);
    return date;
}

export function getFirstWeekdayDate(targetDate: Date) {

    // Month is 0-based (January = 0, December = 11)
    let date = new Date(targetDate.getFullYear(), targetDate.getMonth(), 1); // Last day of the month
    // If it's Saturday (6) or Sunday (0), go back to Friday
    if (date.getDay() === 6)
        date.setDate(date.getDate() + 2);
    else if (date.getDay() === 0)
        date.setDate(date.getDate() + 1);
    return date;
}

export function getDateLocaleEl(date: Date) {
    return date.toLocaleDateString('el', { weekday: 'long' }) + ' ' + getDayAndMonth(date) + "/" + date.getFullYear();
}
export function getDateShortLocaleEl(date: Date) {
    return date.toLocaleDateString('el', { weekday: 'short' }) + ' ' + getDayAndMonth(date) + "/" + date.getFullYear();
}

export function getDateLocale(date: Date, lang: string) {
    return date.toLocaleDateString(localeFromLang(lang as Language), { weekday: 'long' }) + ' ' + getDayAndMonth(date) + "/" + date.getFullYear();
}
export function getDateShortLocale(date: Date, lang: string) {
    return date.toLocaleDateString(localeFromLang(lang as Language), { weekday: 'short' }) + ' ' + getDayAndMonth(date) + "/" + date.getFullYear();
}